import { darken, tint } from 'polished';
import React from 'react';
import styled from 'styled-components';
import { Images } from '../../../@resources/img';
import { tintSafe } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { Icon } from '../../../concepts/resources/Icon';
import { Fill } from '../../../ui/Fill';
import { useResolve } from '../../Container';
import { ValueCard } from '../../packages/hand/ValueCard';
import { Participant } from '../participants/Participant';
import { ParticipantManager } from '../participants/ParticipantManager';
import { Colors } from './../../../ui';
import { RenderTextField } from './RenderField';
import {
    RenderBounds,
    RenderBox,
    RenderConcealed,
    RenderCounter,
    RenderHeadline,
    RenderInner,
    useBracketShortcut,
    usePose
} from './RenderHelpers';

const useParticipant = (userId: string) => {
    return useResolve(ParticipantManager).get(userId);
};

const RenderEmoticon: LFC<{ value: string }> = ({ value }) => {
    return <RenderBox
        inset="0"
        display="flex"
        alignItems="center"
        justifyContent="center">
        <Icon src={`deqo://icons/tokens/${value}@[0.75,0.75,0]`} />
    </RenderBox>;
};

const RenderImage = styled.img`
    display: block;
    width: 80px;
    height: 80px;
    border-radius: 8px;
    background-size: cover;
`;

const RenderInitials = styled.div<{ color: string }>`
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Rubik;
    font-size: 34px;
    font-weight: 450;
    color: white;
    background: ${p => p.color};
    border-radius: 50%;
`;

const RenderUser: LFC<{ value: Participant }> = ({ value }) => {
    return <RenderBox inset="0" display="flex" alignItems="center" justifyContent="center">
        {!!value.photo ? <RenderImage src={value.photo} /> : <RenderInitials color={value.color}>{Participant.toInitials(value.name)}</RenderInitials>}
    </RenderBox>;
};

const RenderUrl: LFC<{ value: string }> = ({ value }) => {
    return <RenderBox inset="0" display="flex" alignItems="center" justifyContent="center">
        <RenderImage src={value} />
    </RenderBox>;
};

const RenderFront: LFC<{ object: ValueCard }> = ({ object, children }) => {
    const { selected, targeted } = usePose(object);
    return <RenderBox
        inset="0"
        borderRadius="8px"
        color={tintSafe(selected ? 0 : (targeted ? .05 : .1), object.color)}
        backgroundColor="white"
        transform={object.concealed ? 'rotateY(180deg)' : ''}
        backfaceVisibility="hidden"
        transition="transform 200ms ease-in-out"
    >
        <RenderBox
            inset="0"
            border={`solid 3px ${selected || targeted ? darken(.05, Colors.primaryLight) : Colors.primaryLight}`}
            borderRadius="8px" />
        {children}

    </RenderBox>;
};

const RenderBack: LFC<{ object: ValueCard }> = ({ object, children }) => {
    const { selected, targeted } = usePose(object);
    // 009688
    return <RenderBox
        inset="0"
        border={`solid 3px ${selected || targeted ? darken(.05, Colors.primaryLight) : Colors.primaryLight}`}
        borderRadius="8px"
        background={`${tint(.75, Colors.primaryLight)} url(${Images.CardBack}) no-repeat center center`}
        backgroundSize="48px 48px"
        transform={object.concealed ? '' : 'rotateY(180deg)'}
        backfaceVisibility="hidden"
        transition="transform 200ms ease-in-out"
    >
        <RenderBox inset="0px" border={'solid 3px white'} borderRadius="5px" />
        {children}
    </RenderBox>;
};

export const ValueCardVisual: LFC<{ object: ValueCard }> = ({ object, children }) => {

    const user = useParticipant(object.userId);
    const bs = useBracketShortcut(object.largeText);
    const { beingEdited, selected, targeted, effects } = usePose(object);

    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <RenderFront object={object}>
                    {!beingEdited && <>
                        <RenderTextField object={object} name="smallText" />
                        <RenderFlipped>
                            <RenderTextField object={object} name="smallText" />
                        </RenderFlipped>
                        {bs.type === 'none' && <RenderTextField object={object} name="largeText" />}
                        {bs.type === 'emoticon' && <RenderEmoticon value={bs.value} />}
                        {bs.type === 'user' && <RenderUser value={bs.value} />}
                        {bs.type === 'url' && <RenderUrl value={bs.value} />}
                    </>}
                </RenderFront>
                <RenderBack object={object} />
                {(selected || targeted) && !beingEdited && !effects?.shuffle && user && !user.self &&
                    <RenderHeadline>
                        <RenderCounter user={user} />
                    </RenderHeadline>
                }
                {(selected || targeted) && !beingEdited && !effects?.shuffle && user?.self &&
                    <RenderHeadline>
                        <RenderConcealed object={object} />
                    </RenderHeadline>
                }
            </RenderInner>
        </RenderBounds>
    </>;
};

const RenderFlipped = styled(Fill)`
    transform: rotate(180deg);
`;
